/**
 * Settings → Messaging (slice 2e spec §6.1; Paper 60, 60b, 65): one row per
 * messaging app (iMessage, for now), the person's own, in its state: not
 * linked, waiting for the code, expired, "Is that you?", linked, or
 * unavailable. Below it, how texting works, and on a desktop what it looks
 * like on the phone.
 *
 * Any person links their own handle; the owner never sees another's. The
 * `messaging` hub event refetches the page, so the row flips live. Below it,
 * slice 2h's Email card (`email-section.tsx`).
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Link as RouterLink } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { Ellipsis } from "lucide-react";
import { useState, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { StateLabel } from "../settings/integrations-parts";
import {
  useConfirmLink,
  useCreateLink,
  useHeldCode,
  useMessaging,
  useRemoveLink,
  type Link,
  type MessagingApp,
} from "./api";
import { useServerSeconds } from "./clock";
import { EmailSection } from "./email-section";
import { LinkWindow, type WindowOpen } from "./link-window";
import { ConfirmRemove } from "./window-parts";
import { ImessageTile } from "./parts";
import { detailOf, phoneStatus, rowState, shownHandle, statusOf, type RowState } from "./view";

export function MessagingTab({ me }: { me: Me }) {
  const query = useMessaging();
  const [win, setWin] = useState<WindowOpen | null>(null);
  // A failed refetch keeps what was last read (a code just made among it).
  const data = query.data;
  const app = data?.apps.find((a) => a.app === "imessage");
  const waiting = Boolean(app?.link && app.link.status !== "linked");
  const now = useServerSeconds(waiting);
  const mobile = useIsMobile();
  return (
    <div className="flex flex-col gap-10 md:gap-8" data-messaging>
      <section aria-label="Text your crew" className="flex flex-col gap-2.5 md:gap-3">
        <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-2.5">
          <h2 className="text-question leading-6 font-semibold text-foreground">Text your crew</h2>
          <p className="text-meta leading-4.5 text-muted-foreground">
            <span className="hidden md:inline">Message your Chief of Staff from your phone. </span>
            Replies come back as texts. Only you see your number.
          </p>
        </div>
        {query.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : !data || !app ? (
          <p className="text-body text-destructive">{errorHint(query.error)}</p>
        ) : (
          <Row app={app} state={rowState(app, data.available, now)} now={now} onWindow={setWin} />
        )}
      </section>
      {data ? <EmailSection me={me} email={data.email} mobile={mobile} /> : null}
      <HowItWorks />
      <LinkWindow
        open={win}
        app={app}
        timezone={me.workspace.timezone}
        onClose={() => setWin(null)}
      />
    </div>
  );
}

/**
 * The row (Paper 65): the tile, the name and its line, the status lane and the
 * actions lane, the same lanes as Integrations. On a phone (Paper 60b) the
 * status goes under the name, and the actions to a band below.
 */
function Row({
  app,
  state,
  now,
  onWindow,
}: {
  app: MessagingApp;
  state: RowState;
  now: number;
  onWindow: (open: WindowOpen) => void;
}) {
  const held = useHeldCode("link" in state ? state.link.id : null)?.code ?? null;
  const detail = detailOf(state, now, held);
  const unavailable = state.kind === "unavailable";
  // A change's refusal (Photon down or full, a handle someone else linked), under the row.
  const [error, setError] = useState<unknown>(null);
  const actions = <Actions app={app} state={state} onWindow={onWindow} onError={setError} />;
  return (
    <div
      data-messaging-app={app.app}
      data-state={state.kind}
      className={cn(
        "flex flex-col rounded-panel border border-border",
        unavailable ? "bg-accent" : "bg-card",
      )}
    >
      <div className="flex items-center gap-3.5 px-4 py-3.5 md:gap-4 md:px-5 md:py-4">
        <ImessageTile muted={unavailable} />
        <Lanes app={app} state={state} detail={detail} actions={actions} onWindow={onWindow} />
      </div>
      <PhoneBand state={state} detail={detail} actions={actions} />
      {error ? (
        <p role="alert" className="px-4 pb-3.5 text-meta text-destructive md:px-5 md:pb-4">
          {errorHint(error)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The name and its line, then the status and actions lanes (Paper 65); on a
 * phone the status under the name, and a linked row's menu at its end.
 */
function Lanes({
  app,
  state,
  detail,
  actions,
  onWindow,
}: {
  app: MessagingApp;
  state: RowState;
  detail: string;
  actions: ReactNode;
  onWindow: (open: WindowOpen) => void;
}) {
  const status = statusOf(state);
  return (
    <>
      <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5">
        <span className="text-body leading-5 font-semibold text-foreground">{app.name}</span>
        <span className="hidden text-meta leading-4.5 text-muted-foreground md:block">
          {detail}
        </span>
        <StateLabel tone={status.tone} className="font-normal text-muted-foreground md:hidden">
          {phoneStatus(state)}
        </StateLabel>
      </div>
      <StateLabel tone={status.tone} className="hidden w-42.5 shrink-0 md:flex">
        {status.text}
      </StateLabel>
      <div className="hidden min-w-40 shrink-0 items-center justify-end gap-1 md:flex">
        {actions}
      </div>
      {state.kind === "linked" ? (
        <div className="md:hidden">
          <LinkedMenu app={app} onWindow={onWindow} />
        </div>
      ) : null}
    </>
  );
}

/** On a phone, the row's line and its buttons, under it (Paper 60b). */
function PhoneBand({
  state,
  detail,
  actions,
}: {
  state: RowState;
  detail: string;
  actions: ReactNode;
}) {
  if (state.kind === "unavailable") {
    return <p className="px-4 pb-4 text-sm text-muted-foreground md:hidden">{detail}</p>;
  }
  const linked = state.kind === "linked" ? state.link : null;
  return (
    <div className="flex flex-col gap-2.5 border-t border-border px-4 pt-3 pb-4 md:hidden">
      <p className="text-sm leading-5 text-copy">
        {linked ? `Your line is ${shownHandle(linked.line)}. Save it as Crew.` : detail}
      </p>
      <div className="flex gap-2 [&>*]:h-auto [&>*]:flex-1 [&>*]:py-2.5 [&>*]:text-sm">
        {linked ? (
          <a href={`sms:${linked.line}`} className={buttonVariants({ size: "lg" })}>
            Text your crew
          </a>
        ) : null}
        {actions}
      </div>
    </div>
  );
}

/** A row's button (Paper 65): 13px, 32px tall; a phone's band makes it wider. */
const ROW_BUTTON = "h-8 px-3 text-meta";

/** The actions lane, per state (Paper 65). */
function Actions({
  app,
  state,
  onWindow,
  onError,
}: {
  app: MessagingApp;
  state: RowState;
  onWindow: (open: WindowOpen) => void;
  /** A refusal for the row to show; null once a change goes through. */
  onError: (error: unknown) => void;
}) {
  const create = useCreateLink();
  const held = useHeldCode("link" in state ? state.link.id : null);
  const newCode = (number: string) =>
    create.mutate(
      { app: app.app, number },
      {
        onSuccess: ({ link }) => {
          onError(null);
          onWindow({ linkId: link.id });
        },
        onError,
      },
    );
  switch (state.kind) {
    case "unavailable":
      return null;
    case "not_linked":
      return (
        <Button className={ROW_BUTTON} onClick={() => onWindow({ linkId: null })}>
          Link iMessage
        </Button>
      );
    case "pending":
      return (
        <PendingActions
          link={state.link}
          held={held}
          busy={create.isPending}
          onShow={() => onWindow({ linkId: state.link.id })}
          onNewCode={() => newCode(state.link.number)}
          onError={onError}
        />
      );
    case "expired":
      return (
        <Button
          className={ROW_BUTTON}
          disabled={create.isPending}
          onClick={() => newCode(state.link.number)}
        >
          New code
        </Button>
      );
    case "confirming":
      return <ConfirmButtons linkId={state.link.id} onError={onError} />;
    case "linked":
      return (
        <>
          {/* Slice 2i: a person's texts start several chats, so Chats, not one thread. */}
          <RouterLink
            href="/chats"
            className={cn(buttonVariants({ variant: "outline" }), ROW_BUTTON, "bg-card")}
          >
            Open Chats
          </RouterLink>
          <span className="hidden md:inline-flex">
            <LinkedMenu app={app} onWindow={onWindow} />
          </span>
        </>
      );
  }
}

/**
 * A waiting code's actions (spec §6.1): Cancel, and Text it and Show code
 * while this tab holds the code; after a reload, New code. Cancel deletes the
 * code waiting, only: a relink's linked one stays.
 */
function PendingActions({
  link,
  held,
  busy,
  onShow,
  onNewCode,
  onError,
}: {
  link: Link;
  held: { code: string; sms_url: string } | null;
  busy: boolean;
  onShow: () => void;
  onNewCode: () => void;
  onError: (error: unknown) => void;
}) {
  const remove = useRemoveLink();
  const outline = cn(buttonVariants({ variant: "outline" }), ROW_BUTTON, "bg-card");
  return (
    <>
      <Button
        variant="ghost"
        className={ROW_BUTTON}
        disabled={remove.isPending}
        onClick={() => remove.mutate(link.id, { onSuccess: () => onError(null), onError })}
      >
        Cancel
      </Button>
      {held ? (
        <>
          <a href={held.sms_url} className={outline}>
            Text it
          </a>
          <Button variant="outline" className={cn(ROW_BUTTON, "bg-card")} onClick={onShow}>
            Show code
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          className={cn(ROW_BUTTON, "bg-card")}
          disabled={busy}
          onClick={onNewCode}
        >
          New code
        </Button>
      )}
    </>
  );
}

/** "Is that you?" on the row: Yes links it, No refuses that handle for a day (spec §3). */
function ConfirmButtons({
  linkId,
  onError,
}: {
  linkId: string;
  onError: (error: unknown) => void;
}) {
  const confirm = useConfirmLink();
  // A refusal leaves it `confirming`: Yes stays pressable, with why above it.
  const answer = (yes: boolean) =>
    confirm.mutate({ id: linkId, yes }, { onSuccess: () => onError(null), onError });
  return (
    <>
      <Button
        variant="ghost"
        className={ROW_BUTTON}
        disabled={confirm.isPending}
        onClick={() => answer(false)}
      >
        No
      </Button>
      <Button className={ROW_BUTTON} disabled={confirm.isPending} onClick={() => answer(true)}>
        Yes, that's me
      </Button>
    </>
  );
}

/** Relink another number, and Unlink, which asks first (Paper 65). */
function LinkedMenu({
  app,
  onWindow,
}: {
  app: MessagingApp;
  onWindow: (open: WindowOpen) => void;
}) {
  const [asking, setAsking] = useState(false);
  const linked = app.linked;
  if (!linked) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              className="size-9 shrink-0 text-muted-foreground md:size-8"
              aria-label={`${app.name}: more`}
            />
          }
        >
          <Ellipsis className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-50 p-1.5">
          <DropdownMenuItem onClick={() => onWindow({ linkId: null })}>
            Relink another number
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onClick={() => setAsking(true)}>
            Unlink
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmRemove
        linkId={linked.id}
        open={asking}
        onOpenChange={setAsking}
        title={`Unlink ${app.name}?`}
        description={`Your crew will stop answering texts from ${shownHandle(linked.handle ?? linked.number)}. The chats your texts started stay in Chats.`}
        keep="Keep it"
        action="Unlink"
        marker="data-unlink-confirm"
      />
    </>
  );
}

const NOTES = [
  {
    title: "Each thing you text or email about gets its own chat",
    phoneTitle: "Each thing gets its own chat",
    line: "A text about something new starts a chat; a follow-up, or a swipe-reply, carries on its chat. A new email starts a chat named for its subject.",
    phoneLine:
      "A new topic starts a chat; a follow-up or a swipe-reply carries on. Each email gets its own.",
  },
  {
    title: "Replies come back whole",
    phoneTitle: "Replies come back whole",
    line: "One text or email once the answer is ready, in plain words. Anything long becomes a doc, with a link.",
    phoneLine:
      "One text or email once the answer is ready. Anything long becomes a doc, with a link.",
  },
  {
    title: "Give it a minute when it's been quiet",
    phoneTitle: "Give it a minute when it's been quiet",
    line: "Your crew's computer sleeps between jobs. A text or email wakes it; the first answer takes a minute.",
    phoneLine: "A text or email wakes your crew's computer; the first answer takes a minute.",
  },
];

/** How texting and email work (Paper 60, 70), and on a desktop the phone beside it. */
function HowItWorks() {
  return (
    <section aria-label="How it works" className="flex items-start gap-14 md:pt-3">
      <div className="flex flex-1 basis-0 flex-col gap-4.5 pt-2 md:gap-5.5">
        <h2 className="font-display text-[26px] leading-7.5 font-normal tracking-tight text-foreground md:text-title md:leading-8.5">
          Text it or email it. Same crew.
        </h2>
        {NOTES.map((n, i) => (
          <div key={n.title} className="flex gap-3 md:gap-3.5">
            <span className="w-5.5 shrink-0 font-mono text-meta leading-5.5 text-copper">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="flex flex-1 flex-col gap-0.5">
              <span className="text-body leading-5.5 font-semibold text-foreground">
                <span className="md:hidden">{n.phoneTitle}</span>
                <span className="hidden md:inline">{n.title}</span>
              </span>
              <span className="text-sm leading-5.25 text-copy">
                <span className="md:hidden">{n.phoneLine}</span>
                <span className="hidden md:inline">{n.line}</span>
              </span>
            </div>
          </div>
        ))}
      </div>
      <PhonePreview />
    </section>
  );
}

/** What it looks like in Messages (Paper 60's phone): a picture, so hidden from readers. */
function PhonePreview() {
  return (
    <div
      aria-hidden
      className="hidden w-90 shrink-0 flex-col gap-2 rounded-[22px] border border-border bg-card px-4.5 pt-5 pb-5.5 font-[system-ui,sans-serif] lg:flex"
    >
      <div className="flex flex-col items-center gap-1 border-b border-border pb-2.5">
        <span className="flex size-9 items-center justify-center rounded-full bg-foreground font-display text-[20px] leading-5 text-background">
          C
        </span>
        <span className="text-xs font-medium text-foreground">Crew</span>
      </div>
      <span className="self-center pt-1.5 text-[11px] leading-3.5 text-hint">
        iMessage · Today 7:42 AM
      </span>
      <span className="max-w-62.5 self-end rounded-[18px] bg-[#0B84FE] px-3 py-2 text-nav text-white">
        Can you move Thursday's mulch delivery to Friday and tell the Garcias?
      </span>
      <span className="self-end pr-1 text-[11px] leading-3.5 text-hint">Read 7:42 AM</span>
      <span className="max-w-67.5 self-start rounded-[18px] bg-[#E9E9EB] px-3 py-2 text-nav whitespace-pre-wrap text-[#111111]">
        {
          "Done. Greenline moved the mulch to Friday, 8 to 10 am. I emailed Ana Garcia to say so.\n\n• Thursday's crew is free now\n• Task: Garcia spring cleanup (Working)"
        }
      </span>
    </div>
  );
}
